import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import LetterheadTemplateManager from '../../components/LetterheadTemplateManager';
import { 
  Building, 
  Upload, 
  Trash2, 
  Save, 
  Loader2, 
  Image as ImageIcon,
  Shield,
  FileText,
  Star,
  QrCode,
  Clock,
  ExternalLink,
  Key,
  Eye,
  EyeOff
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FirmSettingsPage() {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    firm_name: '',
    firm_address: '',
    firm_phone: '',
    firm_email: '',
    firm_rics_registration: '',
    pi_insurer: '',
    pi_policy_number: '',
    pi_cover_amount: '',
    red_book_edition: 'RICS Valuation – Global Standards 2024',
    uk_supplement_edition: 'UK National Supplement January 2024',
    company_logo_path: null,
    company_logo_filename: null
  });
  
  // Review settings state
  const [reviewSettings, setReviewSettings] = useState({
    review_enabled: true,
    review_platform: 'google',
    google_review_url: '',
    trustpilot_review_url: '',
    enable_qr_code: true,
    reminder_enabled: true,
    reminder_delay_days: 7,
    reminder_max_count: 2,
    draft_footer_text: 'We value your feedback. If you are satisfied with this draft report, we would greatly appreciate an honest review of our service.',
    ethical_disclaimer: 'Providing a review is entirely optional and does not affect report issuance.'
  });
  const [savingReview, setSavingReview] = useState(false);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState({
    sendgrid_api_key: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: ''
  });
  const [savingKeys, setSavingKeys] = useState(false);
  const [showSendgridKey, setShowSendgridKey] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [apiKeysStatus, setApiKeysStatus] = useState(null);
  
  useEffect(() => {
    fetchSettings();
    fetchReviewSettings();
    fetchApiKeys();
    fetchApiKeysStatus();
  }, []);
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/compliance/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReviewSettings = async () => {
    try {
      const response = await axios.get(`${API}/review-workflow/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewSettings(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Failed to fetch review settings:', err);
    }
  };
  
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/admin/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Keys are returned masked, we only update if they exist
      setApiKeys(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    }
  };
  
  const fetchApiKeysStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/api-keys/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeysStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch API keys status:', err);
    }
  };
  
  const handleSaveApiKeys = async () => {
    setSavingKeys(true);
    try {
      await axios.put(`${API}/admin/api-keys`, apiKeys, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API keys saved successfully. Services will use new credentials on next request.');
      // Re-fetch to get masked values and status
      fetchApiKeys();
      fetchApiKeysStatus();
      // Refresh Twilio credentials in backend
      try {
        await axios.post(`${API}/auth/refresh-twilio`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (refreshErr) {
        console.log('Twilio refresh skipped or failed:', refreshErr);
      }
    } catch (err) {
      toast.error('Failed to save API keys');
    } finally {
      setSavingKeys(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/compliance/settings`, {
        firm_name: settings.firm_name,
        firm_address: settings.firm_address,
        firm_phone: settings.firm_phone,
        firm_email: settings.firm_email,
        firm_rics_registration: settings.firm_rics_registration,
        pi_insurer: settings.pi_insurer,
        pi_policy_number: settings.pi_policy_number,
        pi_cover_amount: settings.pi_cover_amount,
        red_book_edition: settings.red_book_edition,
        uk_supplement_edition: settings.uk_supplement_edition
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveReviewSettings = async () => {
    setSavingReview(true);
    try {
      await axios.put(`${API}/review-workflow/settings`, reviewSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review settings saved');
    } catch (err) {
      toast.error('Failed to save review settings');
    } finally {
      setSavingReview(false);
    }
  };
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, or WebP image');
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/compliance/settings/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSettings(prev => ({
        ...prev,
        company_logo_path: response.data.logo_path,
        company_logo_filename: file.name
      }));
      
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/compliance/settings/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings(prev => ({
        ...prev,
        company_logo_path: null,
        company_logo_filename: null
      }));
      
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo');
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="firm-settings-title">Firm Settings</h1>
          <p className="text-muted-foreground">Configure your firm details and report settings</p>
        </div>
        
        <Tabs defaultValue="firm" className="w-full">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="firm">Firm Details</TabsTrigger>
            <TabsTrigger value="letterhead">Letterhead</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              <Star className="h-4 w-4 mr-1" />
              Reviews
            </TabsTrigger>
            <TabsTrigger value="api-keys" data-testid="tab-api-keys">
              <Key className="h-4 w-4 mr-1" />
              API Keys
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="firm" className="space-y-6 mt-6">
        
        {/* Company Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-accent" />
              Company Logo
            </CardTitle>
            <CardDescription>
              Upload your company logo to appear on PDF and Word reports
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              {/* Logo Preview */}
              <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                {settings.company_logo_path ? (
                  <img 
                    src={`${BACKEND_URL}/${settings.company_logo_path}`}
                    alt="Company Logo"
                    className="max-w-full max-h-full object-contain p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <ImageIcon className="h-10 w-10 mx-auto mb-2" />
                    <p className="text-sm">No logo uploaded</p>
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex-1 space-y-4">
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    data-testid="logo-upload-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Logo
                      </>
                    )}
                  </Button>
                  
                  {settings.company_logo_path && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleDeleteLogo}
                      data-testid="delete-logo-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
                
                <div className="text-sm text-muted-foreground">
                  <p>Recommended: PNG or JPEG, max 2MB</p>
                  <p>Ideal dimensions: 400x200 pixels</p>
                  {settings.company_logo_filename && (
                    <p className="mt-2 text-green-600">
                      Current: {settings.company_logo_filename}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Firm Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="h-5 w-5 text-accent" />
              Firm Details
            </CardTitle>
            <CardDescription>
              Your firm information will appear on generated reports
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firm_name">Firm Name</Label>
                <Input
                  id="firm_name"
                  value={settings.firm_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, firm_name: e.target.value }))}
                  placeholder="Your Company Ltd"
                  data-testid="firm-name-input"
                />
              </div>
              <div>
                <Label htmlFor="firm_email">Email</Label>
                <Input
                  id="firm_email"
                  type="email"
                  value={settings.firm_email}
                  onChange={(e) => setSettings(prev => ({ ...prev, firm_email: e.target.value }))}
                  placeholder="info@yourcompany.com"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="firm_address">Address</Label>
              <Textarea
                id="firm_address"
                value={settings.firm_address}
                onChange={(e) => setSettings(prev => ({ ...prev, firm_address: e.target.value }))}
                placeholder="123 Business Street&#10;London&#10;SW1A 1AA"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firm_phone">Phone</Label>
                <Input
                  id="firm_phone"
                  value={settings.firm_phone}
                  onChange={(e) => setSettings(prev => ({ ...prev, firm_phone: e.target.value }))}
                  placeholder="+44 20 1234 5678"
                />
              </div>
              <div>
                <Label htmlFor="firm_rics">RICS Registration Number</Label>
                <Input
                  id="firm_rics"
                  value={settings.firm_rics_registration}
                  onChange={(e) => setSettings(prev => ({ ...prev, firm_rics_registration: e.target.value }))}
                  placeholder="RICS Reg No."
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* PI Insurance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-5 w-5 text-accent" />
              Professional Indemnity Insurance
            </CardTitle>
            <CardDescription>
              PI insurance details for valuer declarations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="pi_insurer">Insurer</Label>
                <Input
                  id="pi_insurer"
                  value={settings.pi_insurer}
                  onChange={(e) => setSettings(prev => ({ ...prev, pi_insurer: e.target.value }))}
                  placeholder="Insurance Company"
                />
              </div>
              <div>
                <Label htmlFor="pi_policy">Policy Number</Label>
                <Input
                  id="pi_policy"
                  value={settings.pi_policy_number}
                  onChange={(e) => setSettings(prev => ({ ...prev, pi_policy_number: e.target.value }))}
                  placeholder="POL-123456"
                />
              </div>
              <div>
                <Label htmlFor="pi_cover">Cover Amount</Label>
                <Input
                  id="pi_cover"
                  value={settings.pi_cover_amount}
                  onChange={(e) => setSettings(prev => ({ ...prev, pi_cover_amount: e.target.value }))}
                  placeholder="£1,000,000"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* RICS Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-accent" />
              RICS Standards Reference
            </CardTitle>
            <CardDescription>
              Red Book edition references for compliance statements
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="red_book">Red Book Edition</Label>
                <Input
                  id="red_book"
                  value={settings.red_book_edition}
                  onChange={(e) => setSettings(prev => ({ ...prev, red_book_edition: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="uk_supplement">UK Supplement Edition</Label>
                <Input
                  id="uk_supplement"
                  value={settings.uk_supplement_edition}
                  onChange={(e) => setSettings(prev => ({ ...prev, uk_supplement_edition: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Letterhead Templates */}
        </TabsContent>
        
        <TabsContent value="letterhead" className="space-y-6 mt-6">
        <LetterheadTemplateManager />
        </TabsContent>
        
        {/* Review Settings Tab */}
        <TabsContent value="reviews" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-500" />
                Review Request Settings
              </CardTitle>
              <CardDescription>
                Configure client review requests for draft reports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Review Request */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-semibold">Enable Review Requests</Label>
                  <p className="text-sm text-muted-foreground">Show review options on draft reports</p>
                </div>
                <Switch
                  checked={reviewSettings.review_enabled}
                  onCheckedChange={(checked) => setReviewSettings(prev => ({ ...prev, review_enabled: checked }))}
                  data-testid="toggle-review-enabled"
                />
              </div>
              
              {reviewSettings.review_enabled && (
                <>
                  {/* Platform Selection */}
                  <div className="space-y-2">
                    <Label>Review Platform</Label>
                    <Select
                      value={reviewSettings.review_platform}
                      onValueChange={(value) => setReviewSettings(prev => ({ ...prev, review_platform: value }))}
                    >
                      <SelectTrigger data-testid="select-review-platform">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="google">Google Reviews</SelectItem>
                        <SelectItem value="trustpilot">Trustpilot</SelectItem>
                        <SelectItem value="both">Both Platforms</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Review URLs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(reviewSettings.review_platform === 'google' || reviewSettings.review_platform === 'both') && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <ExternalLink className="h-4 w-4" />
                          Google Review URL
                        </Label>
                        <Input
                          value={reviewSettings.google_review_url || ''}
                          onChange={(e) => setReviewSettings(prev => ({ ...prev, google_review_url: e.target.value }))}
                          placeholder="https://g.page/r/..."
                          data-testid="input-google-review-url"
                        />
                      </div>
                    )}
                    {(reviewSettings.review_platform === 'trustpilot' || reviewSettings.review_platform === 'both') && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <ExternalLink className="h-4 w-4" />
                          Trustpilot Review URL
                        </Label>
                        <Input
                          value={reviewSettings.trustpilot_review_url || ''}
                          onChange={(e) => setReviewSettings(prev => ({ ...prev, trustpilot_review_url: e.target.value }))}
                          placeholder="https://www.trustpilot.com/evaluate/..."
                          data-testid="input-trustpilot-review-url"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* QR Code Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <QrCode className="h-5 w-5 text-slate-600" />
                      <div>
                        <Label className="text-base font-semibold">Enable QR Code</Label>
                        <p className="text-sm text-muted-foreground">Add QR code to draft reports for easy review access</p>
                      </div>
                    </div>
                    <Switch
                      checked={reviewSettings.enable_qr_code}
                      onCheckedChange={(checked) => setReviewSettings(prev => ({ ...prev, enable_qr_code: checked }))}
                      data-testid="toggle-qr-code"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Reminder Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Reminder Settings
              </CardTitle>
              <CardDescription>
                Configure automatic reminders for clients who select "Maybe Later"
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-semibold">Enable Reminders</Label>
                  <p className="text-sm text-muted-foreground">Send email reminders to clients</p>
                </div>
                <Switch
                  checked={reviewSettings.reminder_enabled}
                  onCheckedChange={(checked) => setReviewSettings(prev => ({ ...prev, reminder_enabled: checked }))}
                  data-testid="toggle-reminders"
                />
              </div>
              
              {reviewSettings.reminder_enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reminder Delay (Days)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={reviewSettings.reminder_delay_days}
                      onChange={(e) => setReviewSettings(prev => ({ ...prev, reminder_delay_days: parseInt(e.target.value) || 7 }))}
                      data-testid="input-reminder-delay"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Reminders</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={reviewSettings.reminder_max_count}
                      onChange={(e) => setReviewSettings(prev => ({ ...prev, reminder_max_count: parseInt(e.target.value) || 2 }))}
                      data-testid="input-max-reminders"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Footer Text Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-600" />
                Draft Report Text
              </CardTitle>
              <CardDescription>
                Customize text displayed on draft reports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Footer Text (shown in draft reports)</Label>
                <Textarea
                  value={reviewSettings.draft_footer_text}
                  onChange={(e) => setReviewSettings(prev => ({ ...prev, draft_footer_text: e.target.value }))}
                  rows={3}
                  placeholder="We value your feedback..."
                  data-testid="input-footer-text"
                />
              </div>
              <div className="space-y-2">
                <Label>Ethical Disclaimer</Label>
                <Textarea
                  value={reviewSettings.ethical_disclaimer}
                  onChange={(e) => setReviewSettings(prev => ({ ...prev, ethical_disclaimer: e.target.value }))}
                  rows={2}
                  placeholder="Providing a review is entirely optional..."
                  data-testid="input-ethical-disclaimer"
                />
                <p className="text-xs text-muted-foreground">
                  This disclaimer ensures ethical compliance - reviews are encouraged but never required.
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Save Review Settings */}
          <div className="flex justify-end">
            <Button onClick={handleSaveReviewSettings} disabled={savingReview} data-testid="save-review-settings-btn">
              {savingReview ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Review Settings
                </>
              )}
            </Button>
          </div>
        </TabsContent>
        
        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="space-y-6 mt-6">
          {/* Status Overview */}
          {apiKeysStatus && (
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border ${apiKeysStatus.sendgrid?.configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2">
                  {apiKeysStatus.sendgrid?.configured ? (
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                  ) : (
                    <div className="h-2 w-2 rounded-full bg-amber-500" />
                  )}
                  <span className="font-medium">SendGrid</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {apiKeysStatus.sendgrid?.configured ? 'Configured - Email service active' : 'Not configured'}
                </p>
              </div>
              <div className={`p-4 rounded-lg border ${apiKeysStatus.twilio?.configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2">
                  {apiKeysStatus.twilio?.configured ? (
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                  ) : (
                    <div className="h-2 w-2 rounded-full bg-amber-500" />
                  )}
                  <span className="font-medium">Twilio</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {apiKeysStatus.twilio?.configured 
                    ? `Configured - SMS 2FA active${apiKeysStatus.twilio?.has_phone ? '' : ' (no phone number)'}`
                    : 'Not configured - SMS 2FA unavailable'}
                </p>
              </div>
            </div>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="h-5 w-5 text-amber-500" />
                Email Integration (SendGrid)
                {apiKeysStatus?.sendgrid?.configured && (
                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                )}
              </CardTitle>
              <CardDescription>
                Configure SendGrid API key for sending email notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="sendgrid_key">SendGrid API Key</Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      id="sendgrid_key"
                      type={showSendgridKey ? "text" : "password"}
                      value={apiKeys.sendgrid_api_key}
                      onChange={(e) => setApiKeys(prev => ({ ...prev, sendgrid_api_key: e.target.value }))}
                      placeholder="SG.xxxxxxxx..."
                      data-testid="sendgrid-api-key-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setShowSendgridKey(!showSendgridKey)}
                    >
                      {showSendgridKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">SendGrid Dashboard</a>
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="h-5 w-5 text-green-500" />
                SMS Integration (Twilio)
                {apiKeysStatus?.twilio?.configured && (
                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                )}
              </CardTitle>
              <CardDescription>
                Configure Twilio credentials for SMS notifications and 2FA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="twilio_sid">Account SID</Label>
                  <Input
                    id="twilio_sid"
                    value={apiKeys.twilio_account_sid}
                    onChange={(e) => setApiKeys(prev => ({ ...prev, twilio_account_sid: e.target.value }))}
                    placeholder="ACxxxxxxxx..."
                    data-testid="twilio-sid-input"
                  />
                </div>
                <div>
                  <Label htmlFor="twilio_phone">Phone Number</Label>
                  <Input
                    id="twilio_phone"
                    value={apiKeys.twilio_phone_number}
                    onChange={(e) => setApiKeys(prev => ({ ...prev, twilio_phone_number: e.target.value }))}
                    placeholder="+1234567890"
                    data-testid="twilio-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="twilio_token">Auth Token</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="twilio_token"
                      type={showTwilioToken ? "text" : "password"}
                      value={apiKeys.twilio_auth_token}
                      onChange={(e) => setApiKeys(prev => ({ ...prev, twilio_auth_token: e.target.value }))}
                      placeholder="xxxxxxxx..."
                      data-testid="twilio-token-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setShowTwilioToken(!showTwilioToken)}
                    >
                      {showTwilioToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Get your credentials from <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Twilio Console</a>
              </p>
            </CardContent>
          </Card>
          
          {/* Save API Keys */}
          <div className="flex justify-end">
            <Button onClick={handleSaveApiKeys} disabled={savingKeys} data-testid="save-api-keys-btn">
              {savingKeys ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save API Keys
                </>
              )}
            </Button>
          </div>
        </TabsContent>
        </Tabs>
        
        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
